import debounce from 'lodash/debounce';
import stringUtils from '../../shared/string-utils';

const ONE_SECOND = 1000;
const LETTER_REGEXP = /\w/;
const MAX_PREDICTED_EMAILS_TO_SHOW = 3;
const MOST_POPULAR_EMAIL_DOMAINS = [
    /* Default domains included */
    'aol.com', 'att.net', 'comcast.net', 'facebook.com', 'gmail.com', 'gmx.com', 'googlemail.com',
    'google.com', 'hotmail.com', 'hotmail.co.uk', 'mac.com', 'me.com', 'mail.com', 'msn.com',
    'live.com', 'sbcglobal.net', 'verizon.net', 'yahoo.com', 'yahoo.co.uk',
    /* Other global domains */
    'email.com', 'fastmail.fm', 'games.com', 'gmx.net', 'hush.com', 'hushmail.com', 'icloud.com',
    'iname.com', 'inbox.com', 'lavabit.com', 'love.com', 'outlook.com', 'pobox.com', 'protonmail.ch', 'protonmail.com', 'tutanota.de', 'tutanota.com', 'tutamail.com', 'tuta.io',
    'keemail.me', 'rocketmail.com', 'safe-mail.net', 'wow.com', 'ygm.com',
    'ymail.com', 'zoho.com', 'yandex.com', 'post.com', 'consultant.com', 'myself.com',
    'europe.com', 'dr.com', 'engineer.com', 'asia.com', 'list-manage.com', 'sendgrid.net',
    'live.net', 'freemail.hu', 'gmx.ch', 'fastmail.com', 'mailbox.org',
    /* United States ISP domains */
    'bellsouth.net', 'charter.net', 'cox.net', 'earthlink.net', 'juno.com', 'usa.com',
    /* British ISP domains */
    'btinternet.com', 'virginmedia.com', 'blueyonder.co.uk', 'freeserve.co.uk', 'live.co.uk',
    'ntlworld.com', 'o2.co.uk', 'orange.net', 'sky.com', 'talktalk.co.uk', 'tiscali.co.uk',
    'virgin.net', 'wanadoo.co.uk', 'bt.com',
    /* Russian ISP domains */
    'mail.ru', 'rambler.ru', 'yandex.ru', 'ya.ru', 'list.ru', 'yandex.by', 'tut.by', 'mail.gomel.by',
];

export class EmailTyposChecker {
    constructor() {
        if (!EmailTyposChecker.emailDomainTyposCheckers) {
            EmailTyposChecker.emailDomainTyposCheckers = MOST_POPULAR_EMAIL_DOMAINS
                .flatMap(EmailTyposChecker.getMostCommonEmailDomainTypos);
        }
    }

    predictCorrectEmailsVariant(email) {
        if (this._lastCachedEmail !== email) {
            this._lastCachedEmail = email;
            this._cache = this._predictCorrectEmailsVariant(email);
        }

        return this._cache;
    }

    formatCorrectEmailVariants(emails) {
        return emails
            .slice(0, MAX_PREDICTED_EMAILS_TO_SHOW)
            .join(', ');
    }

    _predictCorrectEmailsVariant(email) {
        if (!email) {
            return [];
        }

        const emailParts = email.split('@');
        const expectedPartsAmount = 2; // <login>@<domain>
        if (emailParts.length !== expectedPartsAmount) {
            return [];
        }

        const [emailLogin, emailDomain] = emailParts;

        if (MOST_POPULAR_EMAIL_DOMAINS.some((popularDomain) => popularDomain === emailDomain)) {
            return [];
        }

        const predictedEmails = EmailTyposChecker.emailDomainTyposCheckers
            .filter(({domain, regexp}) => regexp.test(emailDomain))
            .map(({domain, regexp, correct}) => {
                const resultDomain = domain.replace('\\.', '.');

                if (correct) {
                    return correct(email, resultDomain);
                } else {
                    return `${emailLogin}@${resultDomain}`;
                }
            });

        return [...new Set(predictedEmails)];
    }

    isEmailCorrect(email) {
        const predictedCorrectEmails = this.predictCorrectEmailsVariant(email);
        return predictedCorrectEmails && predictedCorrectEmails.length === 0;
    }

    static getMostCommonEmailDomainTypos(domain) {
        const regexpBase = domain.replace('.', '\\.');
        return [
            ...EmailTyposChecker.getMissingLetterDomainTypoCheckers(regexpBase),
            ...EmailTyposChecker.getExtraLetterDomainTypoCheckers(regexpBase),
            ...EmailTyposChecker.getWrongLetterDomainTypoCheckers(regexpBase),
            ...EmailTyposChecker.getIncorrectPositionOfAtSymbolCheckers(regexpBase),
        ];
    }

    static getMissingLetterDomainTypoCheckers(domain) {
        return EmailTyposChecker.getEmailDomainTypoCheckers(domain,
            (index) => stringUtils.insert(domain, index + 1, '{0}'));
    }

    static getExtraLetterDomainTypoCheckers(domain) {
        const checkers = [];
        const addChecker = (checker) => checkers.push({domain, regexp: this.createTypoRegexp(checker)});

        for (let i = 0; i < domain.length; i++) {
            if (LETTER_REGEXP.test(domain[i])) {
                addChecker(stringUtils.insert(domain, i, '.'));

                if (i === domain.length - 1 || !LETTER_REGEXP.test(domain[i + 1])) {
                    addChecker(stringUtils.insert(domain, i + 1, '.'));
                }
            }
        }

        return checkers;
    }

    static getIncorrectPositionOfAtSymbolCheckers(domain) {
        return [{
            domain,
            regexp: this.createTypoRegexp(`.+?${domain}`),
            correct: (email = '', emailDomain = '') => {
                const domainPosition = email.lastIndexOf(emailDomain);
                const emailLogin = email.replace('@', '').substring(0, domainPosition - 1);
                return `${emailLogin}@${emailDomain}`;
            },
        }];
    }

    static getWrongLetterDomainTypoCheckers(domain) {
        return EmailTyposChecker.getEmailDomainTypoCheckers(domain,
            (index) => stringUtils.replaceChar(domain, index, '.'));
    }

    static getEmailDomainTypoCheckers(domain, generateCheckerRegexp) {
        return domain.split('')
            .map((char, index) => ({char, index}))
            .filter((symbol) => LETTER_REGEXP.test(symbol.char))
            .map((symbol) => ({domain, regexp: this.createTypoRegexp(generateCheckerRegexp(symbol.index))}));
    }

    static createTypoRegexp(regex) {
        return new RegExp(`^${regex}$`);
    }
}
function initEmailInput(app) {
    app.component('email-input', {
        inheritAttrs: false,
        template: '#email-input',
        props: {
            modelValue: {
                type: String,
            },
            inputClasses: {
                type: String,
                required: false,
            },
            inputGroupClasses: {
                type: String,
                required: false,
            },
            isInputGroup: {
                type: Boolean,
                default: false,
            },
            disableTextSuggestions: {
                type: Boolean,
                default: false,
            },
            disableModalSuggestions: {
                type: Boolean,
                required: false,
            },
            onSuggestionAccept: {
                type: Function,
                default: () => {},
                required: false,
            },
        },
        emits: ['update:modelValue'],
        data() {
            return {
                email: this.modelValue,
                visible: false,
                letterRegex: /\w/,
                emailDomainTyposCheckers: null,
                predictedDomains: [],
                debounceEmailValidation: () => { },
                emailTyposChecker: new EmailTyposChecker(),
                isSuggestionModalOpened: false,
                emailSuggestions: '',
            };
        },
        watch: {
            value(newValue) {
                this.email = newValue;
                this.visible = false;
                this.debounceEmailValidation(newValue);
            },
            email(newValue) {
                this.visible = false;
                this.debounceEmailValidation(newValue);
                this.$emit('update:modelValue', newValue);
            },
            modelValue(newValue, oldValue) {
                if (newValue != oldValue && newValue == '') {
                    this.email = '';
                }
            },
        },
        methods: {
            isEmailInvalid(email) {
                this.predictedEmails = this.emailTyposChecker.predictCorrectEmailsVariant(email);

                return this.predictedEmails && this.predictedEmails.length > 0;
            },

            getPredictedEmails() {
                return this.emailTyposChecker.formatCorrectEmailVariants(this.predictedEmails);
            },

            getMostCommonEmailDomainTypos(domain) {
                const regexpBase = domain.replace('.', '\\.');
                return [
                    ...this.getMissingLetterDomainTypoCheckers(regexpBase),
                    ...this.getExtraLetterDomainTypoCheckers(regexpBase),
                    ...this.getWrongLetterDomainTypoCheckers(regexpBase),
                ];
            },

            getMissingLetterDomainTypoCheckers(domain) {
                return this.getEmailDomainTypoCheckers(domain,
                    (index) => stringUtils.insert(domain, index + 1, '{0}'));
            },

            getExtraLetterDomainTypoCheckers(domain) {
                const checkers = [];
                const addChecker = (checker) => checkers.push({domain, regexp: EmailTyposChecker.createTypoRegexp(checker)});

                for (let i = 0; i < domain.length; i++) {
                    if (this.letterRegex.test(domain[i])) {
                        addChecker(stringUtils.insert(domain, i, '.'));

                        if (i === domain.length - 1 || !this.letterRegex.test(domain[i + 1])) {
                            addChecker(stringUtils.insert(domain, i + 1, '.'));
                        }
                    }
                }

                return checkers;
            },

            getWrongLetterDomainTypoCheckers(domain) {
                return this.getEmailDomainTypoCheckers(domain,
                    (index) => stringUtils.replaceChar(domain, index, '.'));
            },

            getEmailDomainTypoCheckers(domain, generateCheckerRegexp) {
                return domain.split('')
                    .map((char, index) => ({char, index}))
                    .filter((symbol) => this.letterRegex.test(symbol.char))
                    .map((symbol) => ({domain, regexp: EmailTyposChecker.createTypoRegexp(generateCheckerRegexp(symbol.index))}));
            },

            _showIfEmailInvalid(email) {
                if (!this.disableModalSuggestions && !this.emailTyposChecker.isEmailCorrect(this.email)) {
                    const predictedCorrectEmails = this.emailTyposChecker.predictCorrectEmailsVariant(this.email);
                    this.emailSuggestions = this.emailTyposChecker.formatCorrectEmailVariants(predictedCorrectEmails);
                    this.isSuggestionModalOpened = true;
                } else if (!this.disableTextSuggestions && this.isEmailInvalid(email)) {
                    this.visible = true;
                }
            },
            closeSuggestionModal() {
                this.isSuggestionModalOpened = false;
            },
        },
        created() {
            this.emailDomainTyposCheckers = MOST_POPULAR_EMAIL_DOMAINS.flatMap(this.getMostCommonEmailDomainTypos);
        },
        mounted() {
            this.debounceEmailValidation = debounce(this._showIfEmailInvalid.bind(this), ONE_SECOND);
        },
    });
}

export default initEmailInput;
